import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Zap, AlertCircle } from "lucide-react";
import { SUBSCRIPTION_PLANS } from "@/types/user";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageContainer } from "@/components/layout/PageContainer";

export default function Pricing() {
  const { userProfile, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");

  const currentRole = userProfile?.role || "guest";
  const isPremium = currentRole === "premium";
  const isPlatinum = currentRole === "platinum";

  const handleSubscribe = (planId: "free" | "premium" | "platinum") => {
    if (!isAuthenticated) {
      setLocation("/");
      return;
    }

    if (planId === "free") {
      // Already free, no action needed
      return;
    }

    // Navigate to subscribe page with plan selection
    setLocation(`/subscribe?plan=${planId}&cycle=${billingCycle}`);
  };

  const getButtonText = (planId: string) => {
    if (!isAuthenticated) return "로그인하고 시작하기";
    if (currentRole === planId) return "현재 플랜";
    if (planId === "free") return "무료로 시작하기";
    if (currentRole === "free") return "업그레이드";
    if (planId === "premium" && (isPremium || isPlatinum)) return "현재 플랜 이상";
    if (planId === "platinum" && isPlatinum) return "현재 플랜";
    return "업그레이드";
  };

  const isButtonDisabled = (planId: string) => {
    if (!isAuthenticated && planId !== "free") return false;
    if (currentRole === planId) return true;
    if (planId === "premium" && isPlatinum) return true;
    return false;
  };

  return (
    <PageContainer size="wide" className="bg-gradient-to-b from-background to-secondary/20 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">영어 학습을 위한 완벽한 플랜</h1>
          <p className="text-xl text-muted-foreground mb-6">
            뉴스로 배우는 실용 영어, 지금 시작하세요
          </p>

          {/* Billing Cycle Toggle */}
          <div className="inline-flex items-center gap-4 bg-secondary p-1 rounded-lg">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-2 rounded-md transition-colors ${
                billingCycle === "monthly"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-background"
              }`}
              data-testid="button-monthly"
            >
              월간 결제
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`px-6 py-2 rounded-md transition-colors ${
                billingCycle === "yearly"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-background"
              }`}
              data-testid="button-yearly"
            >
              연간 결제
              <Badge variant="default" className="ml-2 bg-green-600">
                2개월 무료
              </Badge>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {SUBSCRIPTION_PLANS.map((plan) => {
            const price = billingCycle === "monthly" ? plan.price : plan.priceYearly / 12;
            const totalYearly = plan.priceYearly;
            const savings = billingCycle === "yearly" && plan.price > 0
              ? plan.price * 12 - plan.priceYearly
              : 0;

            return (
              <Card
                key={plan.id}
                className={`relative ${
                  plan.popular ? "border-primary shadow-lg scale-105" : ""
                } ${plan.id === "platinum" ? "border-primary/50" : ""}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="px-4 py-1">
                      <Zap className="h-3 w-3 mr-1" />
                      인기
                    </Badge>
                  </div>
                )}
                {plan.id === "platinum" && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge variant="default" className="px-4 py-1 bg-gradient-to-r from-yellow-600 to-yellow-400">
                      <Crown className="h-3 w-3 mr-1" />
                      추천
                    </Badge>
                  </div>
                )}

                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl">{plan.nameKo}</CardTitle>
                    <Badge variant={plan.id === "free" ? "outline" : "default"}>
                      {plan.name}
                    </Badge>
                  </div>
                  <CardDescription>
                    {plan.id === "free" && "기본 학습 기능 체험"}
                    {plan.id === "premium" && "학습 중시형 - 무제한 학습"}
                    {plan.id === "platinum" && "풀 서비스 - 광고 없는 완벽한 경험"}
                  </CardDescription>
                  <div className="mt-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">
                        ${price.toFixed(2)}
                      </span>
                      <span className="text-muted-foreground">/월</span>
                    </div>
                    {billingCycle === "yearly" && plan.price > 0 && (
                      <div className="mt-1 space-y-1">
                        <p className="text-sm text-muted-foreground">
                          연간 ${totalYearly.toFixed(2)} 청구
                        </p>
                        {savings > 0 && (
                          <p className="text-sm text-green-600 font-medium">
                            ${savings.toFixed(2)} 절약
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  {plan.hasAds && (
                    <Badge variant="outline" className="mt-2 w-fit">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      광고 포함
                    </Badge>
                  )}
                  {!plan.hasAds && (
                    <Badge variant="default" className="mt-2 w-fit bg-green-600">
                      <Check className="h-3 w-3 mr-1" />
                      광고 없음
                    </Badge>
                  )}
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    variant={plan.popular || plan.id === "platinum" ? "default" : "outline"}
                    className="w-full"
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={isButtonDisabled(plan.id)}
                    data-testid={`button-${plan.id}-subscribe`}
                  >
                    {getButtonText(plan.id)}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle>플랜별 상세 기능 비교</CardTitle>
            <CardDescription>모든 플랜에 포함된 기능과 차이점을 확인하세요</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">기능</th>
                    <th className="text-center py-3 px-4">무료</th>
                    <th className="text-center py-3 px-4 bg-primary/5">프리미엄</th>
                    <th className="text-center py-3 px-4">플래티넘</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b">
                    <td className="py-3 px-4">단어장 저장</td>
                    <td className="text-center py-3 px-4">20개 제한</td>
                    <td className="text-center py-3 px-4 bg-primary/5">무제한</td>
                    <td className="text-center py-3 px-4">무제한</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4">리딩 퀴즈</td>
                    <td className="text-center py-3 px-4">주 2회</td>
                    <td className="text-center py-3 px-4 bg-primary/5">무제한</td>
                    <td className="text-center py-3 px-4">무제한</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4">플래시카드</td>
                    <td className="text-center py-3 px-4">하루 20장</td>
                    <td className="text-center py-3 px-4 bg-primary/5">무제한</td>
                    <td className="text-center py-3 px-4">무제한</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4">광고 제거</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-primary/5">❌</td>
                    <td className="text-center py-3 px-4">✅</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4">콘텐츠 다운로드</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-primary/5">❌</td>
                    <td className="text-center py-3 px-4">✅ (PDF)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4">뉴스레터 추천</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-primary/5">✅</td>
                    <td className="text-center py-3 px-4">✅ 우선 발송</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4">AI 해설 음성</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-primary/5">✅</td>
                    <td className="text-center py-3 px-4">✅ 고음질</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">프리미엄 영상 콘텐츠</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-primary/5">❌</td>
                    <td className="text-center py-3 px-4">✅</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">자주 묻는 질문</h2>
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">구독은 언제든지 취소할 수 있나요?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  네, 언제든지 구독을 취소할 수 있습니다. 취소 시 다음 결제일까지는 유료 기능을 계속 이용하실 수 있습니다.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">프리미엄과 플래티넘의 차이는 무엇인가요?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  프리미엄은 모든 학습 기능을 무제한으로 이용할 수 있지만 광고가 표시됩니다.
                  플래티넘은 프리미엄의 모든 기능에 더해 광고가 완전히 제거되고, 콘텐츠 다운로드,
                  고음질 TTS, 프리미엄 영상 콘텐츠 등 추가 혜택이 제공됩니다.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">연간 결제 시 정말 2개월이 무료인가요?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  네! 프리미엄 연간 결제 시 $39.99 (월 $3.33), 플래티넘 연간 결제 시 $69.99 (월 $5.83)로
                  월간 결제 대비 약 2개월치를 절약할 수 있습니다.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">결제 수단은 무엇이 있나요?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Stripe를 통해 신용카드, 직불카드 결제가 가능합니다.
                  안전하고 편리한 결제 환경을 제공합니다.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">무료 회원도 광고를 봐야 하나요?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  네, 무료 회원과 프리미엄 회원은 서비스 운영을 위해 광고가 표시됩니다.
                  광고 없는 환경을 원하시면 플래티넘 플랜을 선택해주세요.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
    </PageContainer>
  );
}
